import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import axios from "axios";
import CONFIG from "@/assets/js/config.custom.js";
import ValidationService from "@/services/ValidationService";
import Select2Multiple from 'v-select2-multiple-component';

export default {
  name: "reportCardGradingEdit",
  components: {
    MainLayout,
    Select2Multiple,
    Loading,
  },
  mixins: [ValidationService],

  data() {
    return {
      isLoading: false,
      submitted: false,
      reportCardItemDetail: {},
      catOAItems: [],
      catCSItems: [],
      students: [],
      noDataMsg: CONFIG.NO_DATA_FOUND,
      proficiencyOptions: Object.entries(CONFIG.PROFICIENCY_OPTIONS).map(([key, value]) => ({
        id: key,
        text: value
      })),
      form: {
        cat4Values: {},
        cat5Values: {},
      },
      editData: {
        acdYearId: '',
        termId: '',
      },
      disabled: false,
    };
  },

  methods: {
    getProficiencyKey(value) {
      if (!value) return "";
      const searchVal = value.trim();
      const entry = Object.entries(CONFIG.PROFICIENCY_OPTIONS).find(
        (pair) => pair[1] === searchVal
      );
      return entry ? entry[0] : searchVal;
    },

    getValidationError(prefix, itemId) {
      const index = this[ prefix === 'category_OA_items' ? 'catOAItems' : 'catCSItems' ]
        .findIndex(i => i.id === itemId);

      const key = `${prefix}.${index}.value`;
      return this.validationErrors[key]?.[0] || null;
    },

    async fetchReportCardItemDetail() {
      try {
        this.isLoading = true;
        const gradingId = this.$route.params.id;
        const response = await axios.get(`/report-card-grading-item/${gradingId}`);
        if (response.data) {
          this.reportCardItemDetail = response.data;

          this.editData.acdYearId = this.reportCardItemDetail.parent_acd_year_id;
          this.editData.termId = this.reportCardItemDetail.term_id;

          const catOAItems = this.reportCardItemDetail.category_OA_items || [];
          const catCSItems = this.reportCardItemDetail.category_CS_items || [];

          this.catOAItems = catOAItems.map((i) => ({ id: i.item_id, name: i.item_name }));
          this.catCSItems = catCSItems.map((i) => ({ id: i.item_id, name: i.item_name }));

          const allItems = [...catOAItems, ...catCSItems];

          const student = {
            grading_id: this.reportCardItemDetail.id,
            student_id: this.reportCardItemDetail.student_id,
            student_no: this.reportCardItemDetail.student_no,
            student_name: this.reportCardItemDetail.student_name,
            items: allItems.map((i) => ({
              item_id: i.item_id,
              item_name: i.item_name,
              value: i.value,
            })),
          };

          this.students = [student];

          const cat4Values = {};
          const cat5Values = {};
          (student.items || []).forEach((i) => {
            const isCat4 = this.catOAItems.some((c) => c.id === i.item_id);
            if (isCat4) {
              const key = this.getProficiencyKey(i.value);
              cat4Values[i.item_id] = key || "";
            } else {
              cat5Values[i.item_id] = i.value || "";
            }
          });
          this.form.cat4Values = cat4Values;
          this.form.cat5Values = cat5Values;
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isLoading = false;
      }
    },

    validateForm() {
      this.submitted = true;
      this.clearValidationErrors();
      
      let isValid = true;
      const errors = {};

      const validKeys = Object.keys(CONFIG.PROFICIENCY_OPTIONS);
      this.catOAItems.forEach((item, index) => {
        const value = this.form.cat4Values[item.id] || '';
        if (value && !validKeys.includes(value)) {
          errors[`category_OA_items.${index}.value`] = [`${item.name} must be one of: ${Object.values(CONFIG.PROFICIENCY_OPTIONS).join(', ')}`];
          isValid = false;
        }
      });

      this.catCSItems.forEach((item, index) => {
        const value = this.form.cat5Values[item.id] || '';
        if (value.length > 10000) {
          errors[`category_CS_items.${index}.value`] = [`${item.name} must not exceed 10000 characters.`];
          isValid = false;
        }
      });

      if (!isValid) {
        this.validationErrors = errors;
      }
      
      return isValid;
    },

    async updateGrading() {
      if (!this.validateForm()) {
        return;
      }

      try {
        this.isLoading = true;
        const gradingId = this.$route.params.id;

        const categoryOAItems = this.catOAItems.map((item) => ({
          item_id: item.id,
          value: CONFIG.PROFICIENCY_OPTIONS[this.form.cat4Values[item.id]] || this.form.cat4Values[item.id] || "",
        }));

        const categoryCSItems = this.catCSItems.map((item) => ({
          item_id: item.id,
          value: this.form.cat5Values[item.id] || "",
        }));
        const payload = {
          parent_acd_year_id: this.reportCardItemDetail.parent_acd_year_id,
          term_id: this.reportCardItemDetail.term_id,
          student_id: this.reportCardItemDetail.student_id,
          category_OA_items: categoryOAItems,
          category_CS_items: categoryCSItems,
        };
        await axios.put(`/report-card-grading-item/${gradingId}`, payload);
        this.$notification.success("Report card grading updated successfully.", {
          timer: 3,
          position: "bottomCenter",
        });
        this.showList();
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.printValidationError(error.response);
        } else {
          this.handleError(error);
        }
      } finally {
        this.isLoading = false;
      }
    },

    showList() {
      this.$router.push({ path: "/report-card-grading" });
    },

    showDetail() {
      const gradingId = this.$route.params.id;
      this.$router.push({ path: `/report-card-grading/${gradingId}/info` });
    },

    handleError(error) {
      if (error.response && error.response.status === 401) {
        this.$store.commit("logout");
        window.location.replace("login");
      } else {
        console.error(error);
      }
    },
  },

  mounted() {
    if (!this.can('REPORTCARD_GRADING_ITEM', 'edit')) {
      this.showList();
      return;
    }
    this.fetchReportCardItemDetail();
  },
};
